import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Form,
  Divider,
  Row,
  Col,
} from 'antd';
import PropTypes from 'prop-types';
import QRCode from 'react-qr-code';
import { DownloadOutlined } from '@ant-design/icons';

import FormDefaultData from './FormDefaultData';
import { collectedDataMap, MULTI_DIVISION_SWITCH_MODAL_COPY} from '../forms.constants';
import {
  getInvalidMultiDivisionFields,
  getInvalidMultiDivisionWorkflowNodes,
  isMultiDivisionForm,
  resolveFormDivisionIds,
} from '../formHelpers';

import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import DisplayText from '../../common/text/DisplayText';
import HoverHelp from '../../common/HoverHelp';
import DivisionSelector from '../../common/inputs/DivisionSelector';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';

import NuxPopover from '../../nux/NuxPopover';

import FormTypeSelector from '../FormTypeSelector';
import FormNumber from './FormNumber';
import FormCheckbox from './FormCheckbox';
import FormImportDrawer from './FormImportDrawer';
import ExternalFormCheckbox from './ExternalFormCheckbox';
import ExternalFormBrandingSettings from './ExternalFormBrandingSettings';
import FormLibraryImportDrawer from '../FormLibrary/FormLibraryImportDrawer';

import {
  FORMS_ADD_DETAIL_TYPE,
  FORMS_ADD_NUX_STEP_1,
  FORMS_ADD_NUX_STEP_1_CHECK_TEXT,
} from '../../nux/nux.constants';

import {
  createNuxEntry,
} from '../../nux/state/nux.actions';

import config from '../../config';
// import {
//   compareToFirstPasswordHelper,
//   validatePasswordFormatHelper,
// } from '../../helpers/helpers';
import FormColorPicker from '../../common/inputs/FormColorPicker';
import FormTemplatesDisplayFieldSettings from '../FormTemplatesDisplayFieldSettings';
import { getBase64ImageFromURL } from '../../projects/ProjectScheduleOfValues/PDF/sov.pdf.helpers';
import { downloadFile } from '../../files/fileHelpers';
import BorderlessButton from '../../common/buttons/BorderlessButton';

let baseEndpoint = 'https://app.ontraccr.com/';
if (config.env === 'alpha') {
  baseEndpoint = 'https://app.alpha.ontraccr.com/';
}

export default function FormDetails({
  formRef,
  name,
  type,
  typeId,
  onCollectedChanged,
  onCollectFullUpdate,
  onDivisionIdsChanged,
  selectedDivisionIds = [],
  sections = [],
  workflow = {},
  canEdit,
  onEditChanged,
  canResubmit,
  onResubmitChanged,
  canReassign,
  onReassignChanged,
  reassignAll,
  onReassignAllChange,
  canSubmitChild,
  onSubmitChildChanged,
  isExternalForm,
  onIsExternalFormChanged,
  generateQRCode,
  onGenerateQRCodeChanged,
  respectAssignedUsers,
  onRespectAssignedUsersChanged,
  onSectionsChanged,
  onWorkflowChange,
  onDrawOptionsChanged,
  onFileMapChanged,
  currentStep,
  collected = {},
  isDisplay = false,
  isAdd = false,
  canAddGlobal, // Project specific forms can be copied in from global.
  canAddFromLibrary, // Global templates can be imported from library
  externalFormURL,
  externalFormLogo,
  externalFormLogoFileType,
  externalFormLogoURL,
  externalFormText,
  onExternalFormLogoChanged,
  onExternalFormLogoFileTypeChanged,
  templateId,
  setLibraryTemplateStatusesToAdd,
  onStandardTemplateChanged,
  setPrevUseStandardTemplate,
  displayFields,
  setDisplayFields,
  fields,
  showInfoBar,
  onShowInfoBarChange,
  setPDFName,
  redirectLink,
  projectId,
  setIsImportedV2Template,
}) {
  const dispatch = useDispatch();

  const activeNuxAction = useSelector((state) => state.nux.activeNuxAction);
  const nux = useSelector((state) => state.nux.nux);
  const isExternalFormEnabled = useSelector((state) => (
    state.settings.company.isExternalFormEnabled
  ));

  const [stateCollect, setStateCollect] = useState({});
  const [formTypeId, setFormTypeId] = useState(typeId);
  const [showImportDrawer, setShowImportDrawer] = useState(false);
  const [externalFormLogoPreview, setExternalFormLogoPreview] = useState(externalFormLogoURL);

  const qrCodeRef = useRef();

  const qrCodeValue = useMemo(() => {
    if (isExternalForm) {
      if (!externalFormURL) return null;
      const fullURL = `${baseEndpoint}${externalFormURL}`;
      return fullURL;
    }
    // For internal forms, generate QR code with formTemplateId and type
    if (templateId && generateQRCode) {
      const qrData = JSON.stringify({
        formTemplateId: templateId,
        type: 'form',
      });
      return qrData;
    }
    return null;
  }, [externalFormURL, isExternalForm, templateId, generateQRCode]);

  const onShowImportClicked = useCallback(() => setShowImportDrawer(true), []);
  const onHideImport = useCallback(() => setShowImportDrawer(false), []);
  const onDivisionIdsChange = useCallback((newDivisionIds = []) => {
    const normalizedIds = Array.isArray(newDivisionIds) ? newDivisionIds : [newDivisionIds].filter(Boolean);
    const wasMultiDivision = isMultiDivisionForm(selectedDivisionIds);
    const isNowMultiDivision = isMultiDivisionForm(normalizedIds);

    onDivisionIdsChanged(normalizedIds);
    if (formRef?.current) {
      formRef.current.setFieldsValue({ divisionIds: normalizedIds });
    }

    if (!wasMultiDivision && isNowMultiDivision) {
      const invalidFields = getInvalidMultiDivisionFields(sections);
      const invalidWorkflowNodes = getInvalidMultiDivisionWorkflowNodes(workflow);
      if (invalidFields.length || invalidWorkflowNodes.length) {
        CustomConfirmModal({
          title: 'Multi-Division Form Restrictions',
          content: MULTI_DIVISION_SWITCH_MODAL_COPY,
          okText: 'OK',
          hideCancel: true,
        });
      }
    }
  }, [onDivisionIdsChanged, selectedDivisionIds, sections, workflow, formRef]);

  const onImportGlobalTemplate = useCallback((updateDivisionId) => async (template = {}) => {
    if (!formRef || !formRef.current) return;
    const {
      divisionId,
      divisionIds: importedDivisionIds,
      allowEdit,
      allowResubmission,
      drawOptions = [],
      formData: {
        name: templateName,
        typeId,
        collected: newCollect = {},
        sections,
        workflow,
        useStandardTemplate = true,
      } = {},
      fileMap,
      pdfName,
      pdfVersion,
    } = template;

    const updateObj = {
      name: templateName,
      typeId,
    };
    // Only force pdfVersion if the imported template is v2
    if (pdfVersion === 2) {
      updateObj.pdfVersion = pdfVersion;
      setIsImportedV2Template(true);
    }
    if (updateDivisionId) {
      const resolvedDivisionIds = resolveFormDivisionIds({
        divisionIds: importedDivisionIds,
        divisionId,
      });
      updateObj.divisionIds = resolvedDivisionIds;
    }

    formRef.current.setFieldsValue(updateObj);

    onStandardTemplateChanged(useStandardTemplate);
    onCollectFullUpdate(newCollect);
    onEditChanged(allowEdit);
    onResubmitChanged(allowResubmission);
    onWorkflowChange(workflow);
    setFormTypeId(typeId);
    setStateCollect(newCollect);
    onDrawOptionsChanged(drawOptions);
    onFileMapChanged(fileMap);
    setPrevUseStandardTemplate(useStandardTemplate);
    setPDFName(pdfName ?? '[Form Name]-[Form Author]-[Date]');

    // Delay this so workflow can update before key.
    // See line 185 and 218 in FormWorkflows.js
    setTimeout(() => {
      formRef.current.setFieldsValue(updateObj);
      if (updateDivisionId) {
        onDivisionIdsChanged(
          resolveFormDivisionIds({ divisionIds: importedDivisionIds, divisionId }),
        );
      }
      onSectionsChanged(sections);
    }, 500);

    setShowImportDrawer(false);
  }, [formRef, onWorkflowChange, onStandardTemplateChanged, setIsImportedV2Template]);

  const onChange = ({ key, checked, subKey }) => {
    onCollectedChanged({ key, data: { [subKey]: checked } });
    setStateCollect({
      ...stateCollect,
      key: {
        ...stateCollect[key],
        [subKey]: checked,
      },
    });
  };
  const onCheckChange = (key) => (checked) => onChange({ key, checked, subKey: 'collect' });
  const onEditableChange = (key) => (checked) => onChange({ key, checked, subKey: 'editable' });

  const onDownloadQR = useCallback(async () => {
    const data = qrCodeRef?.current?.outerHTML;
    const buffer = Buffer.from(data);
    const dataURL = await getBase64ImageFromURL(`data:image/svg+xml;base64,${buffer.toString('base64')}`);
    downloadFile({
      fileURL: dataURL,
      saveName: `${name}_qrcode.png`,
    });
  }, [qrCodeRef, name]);

  const onExternalLogoAdd = useCallback((file) => {
    setExternalFormLogoPreview(URL.createObjectURL(file));
    onExternalFormLogoChanged(file);
    onExternalFormLogoFileTypeChanged(file.type);
  }, [onExternalFormLogoChanged, onExternalFormLogoFileTypeChanged]);

  const onExternalLogoRemove = useCallback(() => {
    setExternalFormLogoPreview(null);
    onExternalFormLogoChanged(null);
    onExternalFormLogoFileTypeChanged(null);
  }, [onExternalFormLogoChanged, onExternalFormLogoFileTypeChanged]);

  useEffect(() => {
    setStateCollect(collected);
  }, [collected]);

  useEffect(() => {
    if (!externalFormLogo) {
      if (externalFormLogoFileType) {
        setExternalFormLogoPreview(externalFormLogoURL);
      } else {
        setExternalFormLogoPreview(null);
      }
    }
  }, [externalFormLogo, externalFormLogoURL, externalFormLogoFileType]);

  useEffect(() => {
    if (
      currentStep === 0
      && isAdd
      && dispatch
      && !nux.has(FORMS_ADD_DETAIL_TYPE)
    ) {
      dispatch(createNuxEntry(FORMS_ADD_DETAIL_TYPE));
      setTimeout(() => {
        // dispatch(startNuxAction(FORMS_ADD_NUX_STEP_1));
      }, 450); // Wait 450 seconds for drawer open animation.
    }
  }, [isAdd, nux, dispatch, currentStep]);

  const showNux = activeNuxAction === FORMS_ADD_NUX_STEP_1;

  const importTitle = canAddGlobal
    ? 'Import from Global Forms'
    : 'Import from Form Library';

  // const compareToFirstPassword = compareToFirstPasswordHelper(formRef, 'externalFormPassword');
  // const validateFormat = validatePasswordFormatHelper(formRef, 'externalFormPasswordConfirm', true);

  return (
    <div className={showNux ? 'form-detail-nux-view' : null}>
      <Row justify="start" gutter={40}>
        <Col span={8}>
          <Form.Item
            name="name"
            label="Form Name"
            rules={[
              { required: !isDisplay, message: 'Please enter the form name' },
            ]}
          >
            {
              isDisplay
                ? <DisplayText title={name} style={{ fontSize: 14, marginBottom: 0 }} />
                : <OnTraccrTextInput />

            }
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="typeId"
            label="Type"
            style={{ marginBottom: 0 }}
            rules={[{ required: !isDisplay, message: 'Select a type' }]}
          >
            <FormTypeSelector
              isDisplay={isDisplay}
              displayValue={type}
              displayStyle={{ marginBottom: 0, fontSize: 14 }}
              onChange={setFormTypeId}
              testId="type"
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          {config.showDivisions && (
            <Form.Item
              name="divisionIds"
              key="divisionIds"
              label="Divisions"
              style={{ marginBottom: 0 }}
              rules={[{ required: !isDisplay, type: 'array', message: 'Select at least one division' }]}
            >
              <DivisionSelector
                displayMode={isDisplay}
                displayStyle={{ marginBottom: 0, fontSize: 14 }}
                mode="multiple"
                divisions={selectedDivisionIds}
                onChange={onDivisionIdsChange}
                disabled={!!projectId}
                testId="division"
              />
            </Form.Item>
          )}
        </Col>
      </Row>
      {/* Hidden field to capture pdfVersion when importing templates */}
      <Form.Item name="pdfVersion" hidden>
        <input type="hidden" />
      </Form.Item>
      <Divider />
      <Form.Item
        name="defaultData"
        label={(
          <Row gutter={10}>
            <Col>
              <HoverHelp
                placement="left"
                content={(
                  <div style={{ width: 300 }}>
                    These fields will automatically be attached to the form by OnTraccr.
                    <br /><br />
                    If you set the field to <b>Editable</b> the user filling out the form
                    will be able to override the automatically attached data.
                  </div>
                )}
              />
            </Col>
            <Col>
              Automatically Collected Data
            </Col>

          </Row>
        )}
      />
      <FormDefaultData title="Date and Time" disabled isDisplay={isDisplay} />
      {!isExternalForm ? (
        <>
          <FormDefaultData title={collectedDataMap.employeeName.title} disabled isDisplay={isDisplay} />
          <FormDefaultData title={collectedDataMap.employeeId.title} disabled isDisplay={isDisplay} />
          <NuxPopover
            placement="left"
            text={FORMS_ADD_NUX_STEP_1_CHECK_TEXT}
            visible={showNux}
          >
            <FormDefaultData
              title="Collect Signature"
              onCheckChange={onCheckChange('employeeSignature')}
              onEditableChange={onEditableChange('employeeSignature')}
              checkName="employeeSignature"
              values={stateCollect.employeeSignature}
              isDisplay={isDisplay}
            />
          </NuxPopover>
          {stateCollect.employeeSignature?.collect && (
            <div style={{ paddingLeft: 24, marginBottom: 4 }}>
              <OnTraccrCheckbox
                style={{ marginTop: 4 }}
                label="Show signature date"
                value={stateCollect.employeeSignature?.showDate}
                onChange={(checked) => onChange({ key: 'employeeSignature', checked, subKey: 'showDate' })}
                disabled={isDisplay}
              />
              <OnTraccrCheckbox
                style={{ marginTop: 4 }}
                label="Show signature time"
                value={stateCollect.employeeSignature?.showTime}
                onChange={(checked) => onChange({ key: 'employeeSignature', checked, subKey: 'showTime' })}
                disabled={isDisplay}
              />
              <OnTraccrCheckbox
                style={{ marginTop: 4 }}
                label="Collect geolocation"
                value={stateCollect.employeeSignature?.collectGeolocation}
                onChange={(checked) => onChange({ key: 'employeeSignature', checked, subKey: 'collectGeolocation' })}
                disabled={isDisplay}
              />
            </div>
          )}
          <FormDefaultData
            title="Geolocation"
            onCheckChange={onCheckChange('geolocation')}
            onEditableChange={onEditableChange('geolocation')}
            checkName="geolocation"
            values={stateCollect.geolocation}
            isDisplay={isDisplay}
          />
          <FormNumber
            typeId={formTypeId}
            onCheckChange={onCheckChange('number')}
            value={stateCollect.number}
            isDisplay={isDisplay}
          />
        </>
      ) : null }
      <br />
      <Form.Item
        name="formOptions"
        label={(
          <Row gutter={10}>
            <Col style={{ fontWeight: 650 }}>
              Form Options
            </Col>
          </Row>
        )}
        style={{ marginBottom: 0 }}
      />
      <FormCheckbox
        title="Allow Edit"
        isDisplay={isDisplay}
        value={canEdit}
        onCheckChange={onEditChanged}
        tooltip={(
          <div style={{ maxWidth: 400 }}>
            Check this box to allow users with <b>Manage Forms</b> permission to
            edit a form during any workflow step.
            <br /><br />
            When a form is edited, the workflow will not be triggered again.
            <br />
            The current workflow will continue with the added changes.
          </div>
        )}
      />
      <FormCheckbox
        title="Allow Resubmission"
        isDisplay={isDisplay}
        value={canResubmit}
        onCheckChange={onResubmitChanged}
        tooltip={(
          <div style={{ maxWidth: 400 }}>
            Check this box to allow users with <b>Manage Forms</b> permission to resubmit a form.
            <br /><br />
            When a form is resubmitted, the workflow will be triggered again.
            <br />
            Emails, notifications and approvals will be re-sent.
            <br />
            Shifts and form triggers will be updated.
          </div>
        )}
      />
      <FormDefaultData
        title="Allow Reassignment"
        isDisplay={isDisplay}
        onCheckChange={onReassignChanged}
        onEditableChange={onReassignAllChange}
        values={{
          collect: canReassign,
          editable: reassignAll,
        }}
        checkName="reassignAll"
      />
      <FormCheckbox
        title="Allow Child Form Submission"
        isDisplay={isDisplay}
        value={canSubmitChild}
        onCheckChange={onSubmitChildChanged}
        tooltip={(
          <div style={{ maxWidth: 400 }}>
            Check this box to allow users to directly
            {' '}
            submit a child form to this parent form.
            <br />
            <br />
            Please note that the available child forms
            {' '}
            are only those that have been configured
            {' '}
            with a &apos;Form Submissions&apos; dropdown field
            {' '}
            tied to this form workflow.
          </div>
        )}
      />
      <ExternalFormCheckbox
        isDisplay={isDisplay}
        value={isExternalForm}
        onCheckChange={onIsExternalFormChanged}
        isExternalFormEnabled={isExternalFormEnabled}
      />
      <Row>
        <Col>
          <FormCheckbox
            title="Generate QR Code"
            isDisplay={isDisplay}
            value={generateQRCode}
            onCheckChange={onGenerateQRCodeChanged}
            tooltip={(
              <div style={{ maxWidth: 400 }}>
                {
                isExternalForm
                  ? 'Check this box to generate a QR code for the external form.'
                  : 'Check this box to generate a QR code for this form. Users will need to be logged into the OnTraccr app to scan and fill out the form.'
                }
              </div>
            )}
          />
        </Col>
        <Col>
          {!isExternalForm && generateQRCode ? (
            <FormCheckbox
              title="Only Assigned Users"
              isDisplay={isDisplay}
              value={respectAssignedUsers}
              onCheckChange={onRespectAssignedUsersChanged}
              tooltip={(
                <div style={{ maxWidth: 400 }}>
                  When enabled, only users assigned to this
                  form will be able to fill it out via QR code scan.
                  When disabled, any logged-in user can fill out the form.
                </div>
              )}
            />
          ) : null }
        </Col>
      </Row>
      <FormCheckbox
        title="Show Info Bar"
        isDisplay={isDisplay}
        value={showInfoBar}
        onCheckChange={onShowInfoBarChange}
        tooltip={(
          <div style={{ maxWidth: 400 }}>
            Check this box to show an info bar on each form indicating
            when a form was triggered and which form triggered it.
            <br />
            <br />
            Having this checked will allow users to view the parent form.
          </div>
        )}
      />
      {(canAddGlobal || canAddFromLibrary) ? (
        <div className="form-template-global-import">
          <OnTraccrButton title={importTitle} onClick={onShowImportClicked} />
        </div>
      ) : null}
      {canAddGlobal && (
        <FormImportDrawer
          visible={showImportDrawer}
          onClose={onHideImport}
          onSubmit={onImportGlobalTemplate(true)}
        />
      )}
      {canAddFromLibrary && (
        <FormLibraryImportDrawer
          visible={showImportDrawer}
          onClose={onHideImport}
          onSubmit={onImportGlobalTemplate(false)}
          setTemplateStatusesToAdd={setLibraryTemplateStatusesToAdd}
        />
      )}
      { !!isExternalForm && (
        <>
          <ExternalFormBrandingSettings
            baseEndpoint={baseEndpoint}
            externalFormURL={externalFormURL}
            externalFormLogoPreview={externalFormLogoPreview}
            externalFormLogoFileType={externalFormLogoFileType}
            externalFormText={externalFormText}
            isDisplay={isDisplay}
            onExternalLogoAdd={onExternalLogoAdd}
            onExternalLogoRemove={onExternalLogoRemove}
          />
          {/* <Form.Item
            name="externalFormPassword"
            label="External Form Password"
            hasFeedback
            rules={[
              { validator: validateFormat }
            ]}
          >
            <OnTraccrTextInput password />
          </Form.Item>
          <Form.Item
            name="externalFormPasswordConfirm"
            label="Confirm External Form Password"
            hasFeedback
            rules={[
              { validator: compareToFirstPassword }
            ]}
          >
            <OnTraccrTextInput password />
          </Form.Item> */}
          <Divider orientation="left">External Form Colors & Redirect URL</Divider>
          <Form.Item
            name="externalFormPrimaryColor"
            label="External Form Primary Color"
            className="form-detail-color-picker"
          >
            <FormColorPicker
              isNotDisplay={!isDisplay}
            />
          </Form.Item>
          <DisplayText title="This is the main color for your external form" />
          <Form.Item
            name="externalFormAccentColor"
            label="External Form Accent Color"
            className="form-detail-color-picker"
          >
            <FormColorPicker
              isNotDisplay={!isDisplay}
            />
          </Form.Item>
          <DisplayText title="Your external form buttons will have this color" />
          <Form.Item
            name="externalFormSecondaryAccentColor"
            label="External Form Secondary Accent Color"
            className="form-detail-color-picker"
          >
            <FormColorPicker
              isNotDisplay={!isDisplay}
            />
          </Form.Item>
          <DisplayText title="Your secondary external form buttons will have this color" />
          <Form.Item
            name="externalFormRedirectLink"
            label="External Form Redirect URL"
            style={{ marginBottom: 0 }}
          >
            {isDisplay ? (
              <DisplayText title={redirectLink} style={{ fontSize: 14, marginBottom: 0 }} />
            ) : (
              <OnTraccrTextInput style={{ width: 300 }} />
            )}
          </Form.Item>
          <DisplayText title="Add a redirect link upon submission of the external form" />
        </>
      )}
      {generateQRCode && qrCodeValue ? (
        <Form.Item
          name="formQRCode"
          label="Form QR Code"
          valuePropName="null"
        >
          <Row>
            <Col>
              <QRCode value={qrCodeValue} level="M" ref={qrCodeRef} style={{ width: 'auto', maxHeight: 128, height: '100%' }} />
            </Col>
            <Col style={{ marginLeft: 15 }}>
              <BorderlessButton
                onClick={onDownloadQR}
                iconNode={<DownloadOutlined />}
              />
            </Col>
          </Row>
        </Form.Item>
      ) : null}
      <br />
      { !!(!isDisplay || displayFields?.length) && (
        <Form.Item
          name="assignedFormDisplay"
          style={{ marginBottom: 0 }}
          label={(
            <Row gutter={10}>
              <Col style={{ fontWeight: 650 }}>
                Assigned/Approved Form Display:
              </Col>
              <Col>
                <HoverHelp
                  placement="right"
                  content={(
                    <div style={{ width: 300 }}>
                      The fields below will be displayed on the assigned and approved forms page.
                      <br /><br />
                      Fields should be entered in the order you want them to appear (left to right),
                      but can be rearranged below.
                    </div>
                  )}
                />
              </Col>
            </Row>
          )}
        />
      )}
      <FormTemplatesDisplayFieldSettings
        isDisplay={isDisplay}
        displayFields={displayFields}
        setDisplayFields={setDisplayFields}
        fields={fields}
      />
    </div>
  );
}

FormDetails.propTypes = {
  formRef: PropTypes.shape({
    current: PropTypes.shape({
      setFieldsValue: PropTypes.func,
    }),
  }),
  name: PropTypes.string,
  type: PropTypes.string,
  typeId: PropTypes.number,
  onCollectedChanged: PropTypes.func,
  onCollectFullUpdate: PropTypes.func,
  onDivisionIdsChanged: PropTypes.func,
  selectedDivisionIds: PropTypes.arrayOf(PropTypes.string),
  sections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    divisionId: PropTypes.string,
  })),
  workflow: PropTypes.shape({
    nodes: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      divisionId: PropTypes.string,
    })),
  }),
  canEdit: PropTypes.bool,
  onEditChanged: PropTypes.func,
  canResubmit: PropTypes.bool,
  onResubmitChanged: PropTypes.func,
  canReassign: PropTypes.bool,
  onReassignChanged: PropTypes.func,
  reassignAll: PropTypes.bool,
  onReassignAllChange: PropTypes.func,
  canSubmitChild: PropTypes.bool,
  onSubmitChildChanged: PropTypes.func,
  isExternalForm: PropTypes.bool,
  onIsExternalFormChanged: PropTypes.func,
  generateQRCode: PropTypes.bool,
  onGenerateQRCodeChanged: PropTypes.func,
  respectAssignedUsers: PropTypes.bool,
  onRespectAssignedUsersChanged: PropTypes.func,
  templateId: PropTypes.string,
  onSectionsChanged: PropTypes.func,
  onWorkflowChange: PropTypes.func,
  onDrawOptionsChanged: PropTypes.func,
  onFileMapChanged: PropTypes.func,
  currentStep: PropTypes.number,
  collected: PropTypes.shape({
    employeeSignature: PropTypes.shape({
      collect: PropTypes.bool,
      editable: PropTypes.bool,
    }),
    geolocation: PropTypes.shape({
      collect: PropTypes.bool,
      editable: PropTypes.bool,
    }),
    number: PropTypes.shape({
      collect: PropTypes.bool,
      editable: PropTypes.bool,
    }),
  }),
  isDisplay: PropTypes.bool,
  isAdd: PropTypes.bool,
  canAddGlobal: PropTypes.bool,
  canAddFromLibrary: PropTypes.bool,
  externalFormURL: PropTypes.string,
  externalFormLogo: PropTypes.shape({}),
  externalFormLogoFileType: PropTypes.string,
  externalFormLogoURL: PropTypes.string,
  externalFormText: PropTypes.arrayOf(PropTypes.string),
  onExternalFormLogoChanged: PropTypes.func,
  onExternalFormLogoFileTypeChanged: PropTypes.func,
  setLibraryTemplateStatusesToAdd: PropTypes.func,
  onStandardTemplateChanged: PropTypes.func,
  setPrevUseStandardTemplate: PropTypes.func,
  displayFields: PropTypes.arrayOf(PropTypes.shape({})),
  setDisplayFields: PropTypes.func,
  fields: PropTypes.arrayOf(PropTypes.shape({})),
  showInfoBar: PropTypes.bool,
  onShowInfoBarChange: PropTypes.func,
  setPDFName: PropTypes.func.isRequired,
  redirectLink: PropTypes.string,
  projectId: PropTypes.string,
  setIsImportedV2Template: PropTypes.func,
};

FormDetails.defaultProps = {
  formRef: null,
  name: '',
  type: '',
  typeId: null,
  onCollectedChanged: null,
  onCollectFullUpdate: null,
  onDivisionIdsChanged: null,
  selectedDivisionIds: [],
  sections: [],
  workflow: {},
  canEdit: false,
  onEditChanged: null,
  canResubmit: false,
  onResubmitChanged: null,
  canReassign: false,
  onReassignChanged: null,
  reassignAll: false,
  onReassignAllChange: null,
  canSubmitChild: false,
  onSubmitChildChanged: null,
  isExternalForm: false,
  onIsExternalFormChanged: null,
  generateQRCode: false,
  onGenerateQRCodeChanged: null,
  respectAssignedUsers: true,
  onRespectAssignedUsersChanged: null,
  templateId: null,
  onSectionsChanged: null,
  onWorkflowChange: null,
  onDrawOptionsChanged: null,
  onFileMapChanged: null,
  currentStep: 0,
  collected: {},
  isDisplay: false,
  isAdd: false,
  canAddGlobal: false,
  canAddFromLibrary: false,
  externalFormURL: '',
  externalFormLogo: null,
  externalFormLogoFileType: null,
  externalFormLogoURL: null,
  externalFormText: [],
  onExternalFormLogoChanged: () => {},
  onExternalFormLogoFileTypeChanged: () => {},
  setLibraryTemplateStatusesToAdd: null,
  onStandardTemplateChanged: null,
  setPrevUseStandardTemplate: null,
  displayFields: [],
  setDisplayFields: null,
  fields: [],
  showInfoBar: false,
  onShowInfoBarChange: null,
  redirectLink: null,
  projectId: null,
  setIsImportedV2Template: null,
};
